/**
 * User type definitions
 * Shared between frontend and backend
 */

export enum UserRole {
  SUPER_ADMIN = 'super_admin',
  CEO = 'ceo',
  CFO = 'cfo',
  DIRECTOR = 'director',
  COO = 'coo',
  CTO = 'cto',
  ADMIN = 'admin',
  // HR = 'hr',
  // MANAGER = 'manager',
  EMPLOYEE = 'employee',
}

export enum UserStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended',
}

export interface IUser {
  _id: string;
  empId?: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  phone?: string;
  avatar?: string;
  role: UserRole;
  department?: string;
  designation?: string;
  reportingManager?: IUser;
  dottedLineManagers?: IUser[];
  hr?: IUser;
  dateOfJoining?: Date;
  altPhone?: string;
  offEmail?: string;
  location?: string;
  religion?: string;
  qualificationAndPassedYear?: string;
  degree?: string;
  presentAddress?: string;
  permanentAddress?: string;
  aadhar?: string;
  pan?: string;
  dob?: Date | string;
  bloodGroup?: string;
  emergencyContact?: {
    name?: string;
    relationship?: string;
    number?: string;
  };
  pastEmployer?: string;
  doe?: Date | string;
  remarks?: string;
  status: UserStatus;
  emailVerified: boolean;
  lastLogin?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role?: UserRole;
  department?: string;
  designation?: string;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export interface AuthResponse {
  user: IUser;
  accessToken: string;
  refreshToken: string;
}

export interface UpdateProfileData {
  // data:{
  //   user:{
  firstName: string;
  lastName: string;
  phone?: string;
  department?: string;
  designation?: string;
  //   }
  // }
}

export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export const EXECUTIVE_ROLES: UserRole[] = [
  UserRole.CEO,
  UserRole.CFO,
  UserRole.DIRECTOR,
  UserRole.COO,
  UserRole.CTO,
];

export const SUPER_ADMIN_ROLES: UserRole[] = [
  UserRole.SUPER_ADMIN,
  ...EXECUTIVE_ROLES,
];

export const ADMIN_ROLES: UserRole[] = [...SUPER_ADMIN_ROLES, UserRole.ADMIN];

export const isExecutiveRole = (role?: string | null): boolean => {
  if (!role) return false;
  const normalized = role.toLowerCase().trim().replace(/[\s-]+/g, '_');
  const knownExecs = ['ceo', 'cfo', 'director', 'coo', 'cto', 'cmm'];
  if (knownExecs.includes(normalized)) return true;
  return (EXECUTIVE_ROLES as string[]).some((r) => r.toLowerCase() === normalized);
};

export const isSuperAdminRole = (role?: string | null): boolean => {
  if (!role) return false;
  const normalized = role.toLowerCase().trim().replace(/[\s-]+/g, '_');
  if (normalized === 'super_admin' || role === UserRole.SUPER_ADMIN) return true;
  return (SUPER_ADMIN_ROLES as string[]).some((r) => r.toLowerCase() === normalized);
};

export const isAdminRole = (role?: string | null): boolean => {
  if (!role) return false;
  const normalized = role.toLowerCase().trim().replace(/[\s-]+/g, '_');
  if (normalized === 'admin' || role === UserRole.ADMIN) return true;
  return (ADMIN_ROLES as string[]).some((r) => r.toLowerCase() === normalized) || isExecutiveRole(role);
};

export const formatRoleLabel = (role?: string | null): string => {
  if (!role) return 'Employee';
  const trimmed = role.trim();
  const lower = trimmed.toLowerCase();
  if (lower === 'super_admin') return 'Super Admin';
  if (lower === 'admin') return 'Admin';
  if (lower === 'director') return 'Director';
  if (['ceo', 'cfo', 'coo', 'cto'].includes(lower)) return lower.toUpperCase();
  if (lower === 'employee') return 'Employee';

  // If role is an abbreviation (2-5 letters, e.g. 'cmm' -> 'CMM', 'vp' -> 'VP')
  if (/^[a-zA-Z]{2,5}$/.test(trimmed)) {
    return trimmed.toUpperCase();
  }

  // Otherwise Title Case words separated by underscores or spaces
  return trimmed
    .replace(/[_-]+/g, ' ')
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

